import React, {useMemo, useState} from 'react';
import {Form, Space} from 'antd';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {
  StyledLegalPersonDocuments,
  StyledLegalPersonDocumentsSpace,
  StyledLegalPersonDocumentsInformation,
  StyledLegalPersonDocumentsImageCard,
  StyledLegalPersonDocumentsUpload
} from './StyledLegalPersonDocuments';
import SpinSmall from '../../../../../SpinSmall';
import {StyledKYCModalContentStepTitle} from '../../../../KYCModal/StyledKYCModal';
import DocumentUploadRules from '../../stepsComponents/DocumentUploadRules';
import docExampleImage from '../../../../../../static/images/pages/kyc/document-example.png';
import {kycHelpers} from '../../../../../../utils/helpers';

const {Item} = Form;

// Two named slots: the registration extract, and the articles of association. Only the visura is
// required -- a rep on a live call may not have the statuto to hand, and blocking on it would
// reintroduce the friction this step exists to remove (H1MVP-4653).
const initialFormValues = {
  visura: undefined,
  statuto: undefined
}

const acceptFileTypes = kycHelpers.getAcceptFileTypes();

const LegalPersonDocuments = React.forwardRef(({
  onChange,
  onSubmit,
  ...rest
}, ref) => {
  const [loading, setLoading] = useState(false);
  const [t] = useTranslation(['main', 'quickStart']);
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState({});

  const rule = (key) => t(`quickStart:kyc.documentUploadRules.${key}`);

  const documentUploadRules = [
    {label: rule('issuingDateNotOlderThan3Months'), variant: 'success'},
    {label: rule('originalFullSizedUnedited'), variant: 'success'},
    {label: rule('notCutBlurryOrReflective'), variant: 'error'},
    {label: rule('notEditedOrExpiredDocument'), variant: 'error'}
  ];

  const {
    description,
    label,
    placeholder,
    validationRequiredRules,
    title
  } = useMemo(() => kycHelpers.getKYCFormDetails({t, formKey: 'lpDocuments'}), [t]);

  const onFinish = () => {
    if (onSubmit) {
      setLoading(true);
      onSubmit(
        documents,
        () => setLoading(false),
        // the parent reports which slot the backend rejected; the copy stays here, where the
        // form-scoped translations live
        ({name, response}) => {
          const errors = kycHelpers.getFileFormErrors({name, response, t});
          setLoading(false);
          form.setFields(errors);
        }
      );
    }
  }

  const handleOnChange = ({name, files}) => {
    const value = files.length > 0 ? files[0] : undefined;
    const updated = {...documents, [name]: value};
    setDocuments(updated);
    form.setFieldValue(name, value);
    onChange && onChange(updated);
  }

  return (
    <StyledLegalPersonDocuments {...rest}>
      <SpinSmall spinning={loading}>
        <StyledLegalPersonDocumentsSpace
          direction='horizontal'
          size={32}
        >
          <StyledLegalPersonDocumentsInformation
            direction='vertical'
            size='middle'
          >
            <StyledKYCModalContentStepTitle>
              {title}
            </StyledKYCModalContentStepTitle>
            <p className='description'>
              {description}
            </p>
            <Space
              direction='vertical'
              size='large'
              style={{width: '100%'}}
            >
              <DocumentUploadRules rules={documentUploadRules} />
              <StyledLegalPersonDocumentsImageCard title={placeholder('image')}>
                <img src={docExampleImage} alt='document example' />
              </StyledLegalPersonDocumentsImageCard>
            </Space>
          </StyledLegalPersonDocumentsInformation>
          <Form
            initialValues={initialFormValues}
            form={form}
            layout='vertical'
            onFinish={onFinish}
            ref={ref}
            requiredMark={false}
          >
            <Item
              label={label('visura')}
              name='visura'
              rules={validationRequiredRules}
            >
              <StyledLegalPersonDocumentsUpload
                onChange={(files) => handleOnChange({name: 'visura', files})}
                uploadProps={{accept: acceptFileTypes}}
              />
            </Item>
            <Item
              label={label('statuto')}
              name='statuto'
            >
              <StyledLegalPersonDocumentsUpload
                onChange={(files) => handleOnChange({name: 'statuto', files})}
                uploadProps={{accept: acceptFileTypes}}
              />
            </Item>
          </Form>
        </StyledLegalPersonDocumentsSpace>
      </SpinSmall>
    </StyledLegalPersonDocuments>
  );
});

LegalPersonDocuments.propTypes = {
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
}

export default LegalPersonDocuments;
